import { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { apiUrl } from "../../../httpClient";
import { handleApiError } from "../../../utils/errorHandler";
import { ADMIN_BASE } from "../../../config";
import type { ProviderStatus, UpdateProviderStatusDto } from "../../../types/api";

export const ProviderStatusActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ProviderStatus>(
    (record?.status as ProviderStatus) || "inactive",
  );
  const [loading, setLoading] = useState(false);

  if (!record) return null;

  const statusOptions: { value: ProviderStatus; label: string; description: string }[] = [
    {
      value: "active",
      label: "Active",
      description: "Provider is operational and accepting requests",
    },
    {
      value: "inactive",
      label: "Inactive",
      description: "Provider is disabled and not accepting requests",
    },
    {
      value: "maintenance",
      label: "Maintenance",
      description: "Provider is temporarily unavailable for maintenance",
    },
    {
      value: "deprecated",
      label: "Deprecated",
      description: "Provider is deprecated and will be removed",
    },
  ];

  const handleSave = async () => {
    if (!record?.id) return;

    setLoading(true);
    try {
      const statusData: UpdateProviderStatusDto = {
        status: selectedStatus,
      };

      const response = await fetch(
        apiUrl(`${ADMIN_BASE}/providers/${record.id}/status`),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("tn_admin_access")}`,
          },
          body: JSON.stringify(statusData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update provider status");
      }

      notify("Provider status updated successfully", { type: "success" });
      setOpen(false);
      refresh();
    } catch (error) {
      const appError = handleApiError(error);
      notify(appError.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        startIcon={<Settings />}
        onClick={() => setOpen(true)}
        size="small"
        variant="outlined"
      >
        Update Status
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Provider Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) =>
                  setSelectedStatus(e.target.value as ProviderStatus)
                }
                label="Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? "Updating..." : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
